/**
 * Rich Text Editor Utility Service
 * 
 * Provides reusable TinyMCE editor configuration for the entire application.
 * This ensures consistent editor setup across all components.
 * 
 * @example
 * ```typescript
 * import { RichTextEditorUtil } from '@core/services/util/rich-text-editor.util';
 * 
 * // In component
 * tinyMceConfig = RichTextEditorUtil.getTinyMceConfig();
 * ```
 */
export class RichTextEditorUtil {
  /**
   * TinyMCE Cloud API Key
   */
  static readonly TINYMCE_API_KEY = "4kifuextoo9cdxpi0b4whovoi9nmke4r3s215xgdne87vuo2";

  /**
   * Alias for default configuration for transition from Quill
   */
  static getEditorConfig(): Record<string, unknown> {
    return this.getTinyMceConfig();
  }

  /**
   * Default TinyMCE configuration
   */
  static getTinyMceConfig(placeholder: string = 'Type here...'): Record<string, unknown> {
    return {
      height: 400,
      menubar: 'edit view insert format table help',
      placeholder: placeholder,
      plugins: [
        'advlist', 'autolink', 'lists', 'link', 'image', 'charmap', 'preview',
        'anchor', 'searchreplace', 'visualblocks', 'code', 'fullscreen',
        'insertdatetime', 'media', 'table', 'code', 'help', 'wordcount',
        'emoticons', 'directionality', 'quickbars'
      ],
      toolbar: 'undo redo | blocks | ' +
        'bold italic forecolor | alignleft aligncenter ' +
        'alignright alignjustify | bullist numlist outdent indent | ' +
        'table image link | removeformat | help',
      content_style: 'body { font-family:Poppins,Helvetica,Arial,sans-serif; font-size:14px } table { border-collapse: collapse; width: 100%; border: 1px solid #ccc; } th, td { border: 1px solid #ccc; padding: 8px; }',
      branding: false,
      promotion: false,
      table_toolbar: 'tableprops tabledelete | tableinsertrowbefore tableinsertrowafter tabledeleterow | tableinsertcolbefore tableinsertcolafter tabledeletecol',
      table_use_colgroups: true,
      table_default_attributes: {
        border: '1'
      },
      table_default_styles: {
        'border-collapse': 'collapse',
        'width': '100%'
      },
      // Ensure pasted tables from Excel/Word get borders
      paste_postprocess: (editor: any, args: any) => {
        const tables = args.node.querySelectorAll('table');
        tables.forEach((table: HTMLElement) => {
          table.setAttribute('border', '1');
          table.style.borderCollapse = 'collapse';
          table.style.width = '100%';
          
          // Ensure all cells have internal borders if missing
          const cells = table.querySelectorAll('td, th');
          cells.forEach((cell: any) => {
            if (!cell.style.border) {
              cell.style.border = '1px solid #ccc';
            }
          });
        });
      },
      // Better image handling
      image_advtab: true,
      image_title: true,
      automatic_uploads: true,
      file_picker_types: 'image',
      // Base configuration for template's local TinyMCE assets
      base_url: '/assets/plugins/custom/tinymce',
    };
  }

  /**
   * Get full editor configuration (all features)
   */
  static getFullConfig(): Record<string, unknown> {
    return this.getTinyMceConfig();
  }

  /**
   * Get minimal editor configuration (basic formatting only)
   */
  static getMinimalConfig(): Record<string, unknown> {
    return {
      height: 200,
      menubar: false,
      plugins: ['lists', 'link', 'wordcount'],
      toolbar: 'bold italic | bullist numlist | link | removeformat',
      branding: false,
      promotion: false,
      content_style: 'body { font-family:Poppins,Helvetica,Arial,sans-serif; font-size:14px }'
    };
  }
}
